import React, { useState, useEffect, useMemo, useContext } from 'react';
import { string, array, bool, func } from 'prop-types';
import { useTranslation } from 'react-i18next';
import LazyLoad, { forceCheck } from 'react-lazyload';

import useStyles from 'hooks/useStyles';
import useGetCurrentImageType from 'hooks/useGetCurrentImageType';

import ApplicationContext from 'components/ApplicationContext';
import LoadingSkeleton from 'components/LoadingSkeleton';
import Button from 'components/Button';
import { ChevronIcon } from 'components/Icon';
import ClassCard from 'components/ClassCard';

// eslint-disable-next-line css-modules/no-unused-class
import styles from './ClassList.scss';

const getLazyLoadSettings = ({ isMobile, isTablet }) => {
  if (isMobile) {
    return { height: 490, offset: 0 };
  }
  if (isTablet) {
    return { height: 440, offset: 0 };
  }
  return { height: 400, offset: 0 };
};

const ClassList = ({
  id,
  list,
  onLinkClick,
  onVideoStart,
  onVideoEnd,
  showSeeMore,
  onSeeMoreClick,
}) => {
  useStyles(styles);
  const { imageType, setContainerRef } = useGetCurrentImageType();
  const [currentPlayVideoId, setCurrentPlayVideoId] = useState(null);
  const { screenWidth } = useContext(ApplicationContext);
  const lazyLoadSettings = useMemo(() => getLazyLoadSettings(screenWidth), [
    screenWidth,
  ]);

  const { t } = useTranslation(['regularClass']);

  const handleVideoStart = item => {
    setCurrentPlayVideoId(item.id);
    onVideoStart(item);
  };

  const handleVideoEnd = item => {
    setCurrentPlayVideoId(null);
    onVideoEnd(item);
  };

  useEffect(() => {
    forceCheck();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={setContainerRef} className={styles.root} id={id}>
      {list.map((item, index) => (
        <LazyLoad
          key={item.id}
          placeholder={<LoadingSkeleton className={styles.loading} />}
          className={styles.card}
          {...lazyLoadSettings}
        >
          <ClassCard
            onLinkClick={() => onLinkClick(item, index)}
            onVideoStart={() => handleVideoStart(item, index)}
            onVideoEnd={() => handleVideoEnd(item, index)}
            forceVideoStop={
              currentPlayVideoId !== null && currentPlayVideoId !== item.id
            }
            {...item}
            key={item.id}
            pictureUrl={item.picture[imageType]}
          />
        </LazyLoad>
      ))}
      {showSeeMore && (
        <Button className={styles.moreBtn} onClick={onSeeMoreClick} reversed>
          {t('common.seeMore')}
          <ChevronIcon width={16} height={16} className={styles.icon} />
        </Button>
      )}
    </div>
  );
};

ClassList.propTypes = {
  id: string,
  onSeeMoreClick: func.isRequired,
  onLinkClick: func.isRequired,
  onVideoStart: func.isRequired,
  onVideoEnd: func.isRequired,
  list: array.isRequired,
  showSeeMore: bool.isRequired,
};

ClassList.defaultProps = {
  id: '',
};

export default ClassList;
